/**
 * Session storage seam.
 *
 * The api layer reads a bearer token through this module so it stays decoupled
 * from where the token comes from. There are two sources, in precedence order:
 *
 *   1. A real OAuth session minted by signing in. Its access token lives in
 *      memory only; its refresh token and non-secret metadata persist in
 *      localStorage so a reload resumes the session without a fresh sign-in.
 *      The auth module owns the OAuth flow and feeds the result in here.
 *   2. A legacy developer token: localStorage under config.tokenStorageKey, or
 *      config.devToken baked into config. This is the fallback for local
 *      development against a gateway, exposed on the console as
 *      plantmewallet.setToken. A real session takes precedence over it.
 *
 * Where tokens live and why it is acceptable: the access token is held in JS
 * memory and never persisted; the refresh token and metadata persist in
 * localStorage. Both are reachable by any script that runs on this origin, so
 * the security posture rests on the strict same-origin Content-Security-Policy
 * the page sets (script-src 'self', no inline scripts, object-src 'none',
 * base-uri 'none') and the self-hosted bundle that adds no third-party script
 * origin. No provider client secret is ever in the browser: the gateway holds
 * every secret and performs the code exchange. The JWTs here are bearer
 * credentials authorizing gateway calls, not spendable key material; signing
 * stays gated by MPC and policy on the gateway. A refresh token is higher value
 * than a dev read token, so it is removed on logout and when a refresh is
 * rejected.
 */

import { config } from '@/config';
import { rememberAccountOnDevice } from '@/lib/accountOnDevice';
import {
  setAccount,
  clearAccount,
  setFreshLogin,
  setPasskeySignup,
  beginSession,
} from '@/store/store';

/** The login result recorded by {@link storeLogin}. */
export interface LoginSession {
  /** Short-lived access JWT. */
  accessToken: string;
  /** Long-lived refresh JWT. */
  refreshToken: string;
  /** Server-side session id, for metadata. */
  sessionId?: string;
  /** Organization the session is scoped to. */
  orgId: string;
  /** Provider that authenticated the user. */
  provider: string;
}

/** The rotated token pair recorded by {@link storeRefreshedPair}. */
export interface RefreshedPair {
  /** New access JWT. */
  accessToken: string;
  /** New refresh JWT (rotated on use). */
  refreshToken: string;
}

/** The non-secret session metadata persisted alongside the refresh token. */
export interface SessionMeta {
  orgId: string | null;
  provider: string | null;
  sessionId: string | null;
}

/**
 * The active access token. For a real session this is set by {@link storeLogin}
 * and rotated by {@link storeRefreshedPair}; it is never written to storage. For
 * the legacy fallback it is resolved once from localStorage or config. Kept in a
 * module variable, not the store, so the api layer can read it through a plain
 * function with no store-shape coupling.
 */
let currentAccess: string | null = null;

/**
 * True once a real OAuth session is active. While true, the legacy dev-token
 * fallback is not consulted, so a stale dev token cannot shadow a real session.
 */
let realSession = false;

/** True once the initial fallback resolution from localStorage and config ran. */
let fallbackResolved = false;

/**
 * Read a value from localStorage, tolerating an access that throws (for example
 * a privacy mode that blocks storage).
 *
 * @param key The storage key.
 * @returns The stored value trimmed, or null when absent or unreadable.
 */
function readStored(key: string): string | null {
  try {
    if (typeof localStorage === 'undefined') return null;
    const value = localStorage.getItem(key);
    return value && value.trim() !== '' ? value.trim() : null;
  } catch {
    // A blocked or unavailable localStorage is not fatal: the app falls back to
    // config.devToken and otherwise shows the login screen.
    return null;
  }
}

/**
 * Write or remove a localStorage value, tolerating a storage access that throws.
 *
 * @param key The storage key.
 * @param value The value to persist, or null to remove the key.
 */
function writeStored(key: string, value: string | null): void {
  try {
    if (typeof localStorage === 'undefined') return;
    if (value) localStorage.setItem(key, value);
    else localStorage.removeItem(key);
  } catch {
    // Persisting is best-effort; the in-memory state still drives this session.
  }
}

/**
 * Resolve the legacy fallback token from its sources once, caching the result.
 * The localStorage value wins over config.devToken so a developer can override
 * the baked-in default without editing source. Only consulted when no real
 * session is active.
 *
 * @returns The resolved fallback token, or null when none is set.
 */
function resolveFallback(): string | null {
  if (fallbackResolved) return currentAccess;
  fallbackResolved = true;
  if (realSession) return currentAccess;
  // The fallback is a development convenience only. In a production build a
  // stale stored token must not masquerade as a session: it would keep the
  // account slice authenticated after a real session expires, so the boot gate
  // would keep the app shell up and fail every call instead of routing to the
  // login screen.
  if (config.flags.devTokenFallback) {
    const stored = readStored(config.tokenStorageKey);
    const fromConfig =
      config.devToken && config.devToken.trim() !== '' ? config.devToken.trim() : null;
    currentAccess = stored ?? fromConfig;
  }
  syncAccount();
  return currentAccess;
}

/**
 * Mirror the current session presence into the account store slice so the UI can
 * reflect a connected or disconnected state. Only the presence of a token and
 * the non-secret metadata are reflected; the token value is the api layer's
 * concern.
 */
function syncAccount(): void {
  // A resumed real session counts as authenticated even before an access token is
  // in memory. The access token is never persisted, so after the reload that
  // follows the OAuth callback a resumed session has only its refresh token until
  // the api layer's proactive refresh mints a fresh access token on the first
  // authed read. Gating authentication on the access token alone would bounce a
  // just-signed-in user back to the login screen on that reload. Every gateway
  // call still carries a real JWT, so this only governs which screen renders, not
  // any server-side access.
  if (realSession || currentAccess) {
    const meta = realSession ? getSessionMeta() : null;
    setAccount({
      token: currentAccess,
      authenticated: true,
      orgId: meta?.orgId ?? null,
      userId: null,
    });
  } else {
    clearAccount();
  }
}

/**
 * The auth-token provider the api layer installs. Returns the current bearer
 * token or null when no session is active.
 *
 * @returns The active access token, or null.
 */
export function getToken(): string | null {
  if (realSession) return currentAccess;
  return resolveFallback();
}

/**
 * Whether a token is currently available, so the boot gate can choose between
 * the app and the login screen without reading the token value.
 *
 * @returns True when a bearer token is set.
 */
export function hasToken(): boolean {
  return getToken() !== null;
}

/**
 * Whether a real OAuth session is active, as opposed to the legacy dev-token
 * fallback. Lets the UI offer logout only for a real session.
 *
 * @returns True when signed in through OAuth.
 */
export function hasRealSession(): boolean {
  return realSession && currentAccess !== null;
}

/**
 * Record a freshly minted OAuth session. The access token is held in memory; the
 * refresh token and non-secret metadata persist so a reload resumes the session.
 *
 * @param session The login result.
 */
export function storeLogin({ accessToken, refreshToken, sessionId, orgId, provider }: LoginSession): void {
  realSession = true;
  fallbackResolved = true;
  currentAccess = accessToken && accessToken.trim() !== '' ? accessToken.trim() : null;
  writeStored(config.refreshStorageKey, refreshToken ?? null);
  const meta: SessionMeta = { orgId: orgId ?? null, provider: provider ?? null, sessionId: sessionId ?? null };
  writeStored(config.sessionMetaStorageKey, JSON.stringify(meta));
  syncAccount();
  // Reset both one-time onboarding flags on every login, then set the one this
  // login warrants. Resetting here is what stops a stale flag from leaking across
  // logins: a passkey signup earlier in this session set passkeySignup, and without
  // this reset a later OAuth login would still be treated as one and lose its
  // create-a-passkey offer. syncAccount above already ran, so these apply to the
  // new session.
  setPasskeySignup(false);
  setFreshLogin(false);
  // Open a new session epoch and drop the previous account's device list. Doing it
  // here rather than on logout is what covers an account swap with no logout in
  // between, which never flips `authenticated`.
  beginSession();
  // A fresh OAuth or OTP login (never a passkey) can be offered the one-time
  // create-a-passkey prompt. THIS LINE is what keeps that offer away from a passkey
  // signup — provider is 'passkey', so freshLogin stays false and the gate fails on
  // its first condition. passkeySignup, set after this returns, is a redundant
  // second guard.
  if (provider !== 'passkey') setFreshLogin(true);
  // Every route to a session ends here — passkey signup, passkey login, and the
  // OAuth exchange all call storeLogin — so this is the one place that records "an
  // account has been used in this browser" without the call sites drifting apart.
  rememberAccountOnDevice();
}

/**
 * Replace the access and refresh tokens after a refresh rotation, keeping the
 * existing session metadata.
 *
 * @param pair The rotated token pair.
 */
export function storeRefreshedPair({ accessToken, refreshToken }: RefreshedPair): void {
  realSession = true;
  fallbackResolved = true;
  currentAccess = accessToken && accessToken.trim() !== '' ? accessToken.trim() : null;
  if (refreshToken && refreshToken.trim() !== '') {
    writeStored(config.refreshStorageKey, refreshToken.trim());
  }
  syncAccount();
}

/**
 * Read the persisted refresh token for the active session.
 *
 * @returns The refresh token, or null when none is stored.
 */
export function getRefreshToken(): string | null {
  return readStored(config.refreshStorageKey);
}

/**
 * Read the persisted non-secret session metadata.
 *
 * @returns The metadata object, or null when none is stored or it is unparseable.
 */
export function getSessionMeta(): SessionMeta | null {
  const raw = readStored(config.sessionMetaStorageKey);
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw) as Record<string, unknown>;
    if (parsed && typeof parsed === 'object') {
      return {
        orgId: typeof parsed.orgId === 'string' ? parsed.orgId : null,
        provider: typeof parsed.provider === 'string' ? parsed.provider : null,
        sessionId: typeof parsed.sessionId === 'string' ? parsed.sessionId : null,
      };
    }
  } catch {
    // A corrupt metadata blob is treated as no metadata; the session still works
    // off its in-memory access token and persisted refresh token.
  }
  return null;
}

/**
 * Resume a persisted real session on boot. The access token is not persisted, so
 * resumption marks the session as real and relies on the api layer's proactive
 * refresh to mint a fresh access token from the stored refresh token before the
 * first authed read. Returns whether a refresh token was found to resume from.
 *
 * @returns True when a persisted refresh token exists to resume.
 */
export function resumePersistedSession(): boolean {
  const refresh = readStored(config.refreshStorageKey);
  if (!refresh) return false;
  // A stored refresh token is itself proof that an account was used here, and this
  // path never reaches storeLogin. Recorded before the token is validated on
  // purpose: whether it still works decides if the session resumes, not whether an
  // account once existed in this browser.
  rememberAccountOnDevice();
  realSession = true;
  fallbackResolved = true;
  currentAccess = null;
  syncAccount();
  return true;
}

/**
 * Clear the active OAuth session: wipe the in-memory access token, the persisted
 * refresh token, and the session metadata. The legacy dev-token key is left
 * intact so a developer fallback is not silently wiped by a logout; use
 * {@link clearToken} to remove that.
 */
export function clearSession(): void {
  realSession = false;
  currentAccess = null;
  writeStored(config.refreshStorageKey, null);
  writeStored(config.sessionMetaStorageKey, null);
  // Re-resolve the legacy fallback so a configured dev token reconnects the
  // read-only client after a logout rather than dropping straight to the login
  // screen when one is present. resolveFallback is dev-gated, so in a
  // production build this resolves to no token and the account slice goes
  // unauthenticated, routing the boot gate to the login screen.
  fallbackResolved = false;
  resolveFallback();
  syncAccount();
}

/**
 * The legacy developer device fingerprint, lazily created and persisted so it is
 * stable for this browser install. It binds a session to this install in the
 * gateway's audit trail. It is an opaque random value, not a secret.
 *
 * @returns The install-scoped device fingerprint.
 */
export function getDeviceFingerprint(): string {
  const existing = readStored(config.deviceStorageKey);
  if (existing) return existing;
  const generated =
    typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function'
      ? crypto.randomUUID()
      : `dev-${Date.now()}-${Math.floor(Math.random() * 1e9)}`;
  writeStored(config.deviceStorageKey, generated);
  return generated;
}

/**
 * Set or clear the legacy developer fallback token from within the app. Persists
 * a non-empty token to localStorage so it survives a reload, and removes the key
 * when cleared. Exposed on window as plantmewallet.setToken by the boot code so a
 * developer can connect from the console without a build change. Has no effect on
 * an active real OAuth session beyond updating the fallback used after logout.
 *
 * @param token The bearer token to use, or null to disconnect.
 */
export function setToken(token: string | null): void {
  const next = token && token.trim() !== '' ? token.trim() : null;
  writeStored(config.tokenStorageKey, next);
  if (!realSession) {
    fallbackResolved = true;
    currentAccess = next;
    syncAccount();
  }
}

/** Clear the legacy developer fallback token and its persisted key. */
export function clearToken(): void {
  setToken(null);
}
